import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAPI } from '../hooks/useAPI';
import { contentAPI } from '../services/api';
import { getImageUrl } from '../config/api';
import { useLocation } from 'react-router-dom';

const CheckIcon = () => (
  <svg className="h-6 w-6 text-lime-500 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const ImageGallery = ({ images, title }) => {
  if (!images || images.length === 0) return null;

  return (
    <div>
      <h4 className="text-2xl font-bold text-gray-900 mb-6">Gallery</h4>
      <div className={`grid gap-6 ${images.length === 1 ? 'grid-cols-1 max-w-2xl' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
        {images.map((img, idx) => (
          <figure key={idx} className="group overflow-hidden rounded-lg shadow-md bg-gray-100">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={getImageUrl(img.src)}
                alt={img.caption || `${title} image ${idx + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            {img.caption && (
              <figcaption className="px-4 py-3 text-sm text-gray-700 font-medium bg-white">
                {img.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </div>
  );
};

const ServiceSection = ({ service, index }) => {
  const isEven = index % 2 === 0;

  return (
    <article id={service.slug} className="scroll-mt-24 bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Hero banner */}
      {service.heroImage && (
        <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden">
          <img
            src={getImageUrl(service.heroImage)}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10 lg:p-12">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-3">
              {service.title}
            </h2>
            <p className="text-lg md:text-xl text-gray-100 font-medium max-w-3xl">
              {service.shortDescription}
            </p>
          </div>
        </div>
      )}

      <div className="px-8 md:px-10 lg:px-12 py-10 md:py-12 space-y-12">
        {/* Overview */}
        <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-start`}>
          <div className="flex-1">
            <p className="text-lg text-gray-900 font-medium leading-relaxed">
              {service.description}
            </p>
          </div>
          {service.highlights && service.highlights.length > 0 && (
            <div className="w-full lg:w-80 flex-shrink-0 space-y-4">
              {service.highlights.map((item, idx) => (
                <div key={idx} className="bg-teal-50 p-5 rounded-lg border border-teal-100">
                  <h5 className="text-sm font-bold text-teal-900 uppercase tracking-wider mb-1">
                    {item.label}
                  </h5>
                  <p className="text-gray-900 font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image gallery */}
        <ImageGallery images={service.images} title={service.title} />

        {/* Benefits */}
        {service.benefits && service.benefits.length > 0 && (
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6">Key Benefits</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {service.benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start">
                  <CheckIcon />
                  <p className="text-gray-900 font-medium">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Product range */}
        {service.products && service.products.length > 0 && (
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6">Product Range</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {service.products.map((product, idx) => (
                <div key={idx} className="bg-gradient-to-br from-teal-50 to-lime-50 p-6 rounded-lg border border-teal-100">
                  <h5 className="font-bold text-lg text-teal-900 mb-2">{product.name}</h5>
                  <p className="text-gray-700 font-medium">{product.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Specifications */}
        {service.specifications && service.specifications.length > 0 && (
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6">Specifications & Standards</h4>
            <ul className="bg-gray-50 p-6 rounded-lg space-y-3">
              {service.specifications.map((spec, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="inline-block bg-teal-900 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 mt-0.5">✓</span>
                  <span className="text-gray-900 font-medium">{spec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Process */}
        {/* {service.process && service.process.length > 0 && (
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.process.map((step) => (
                <div key={step.step} className="bg-gradient-to-br from-teal-50 to-lime-50 p-6 rounded-lg border border-teal-100">
                  <div className="bg-teal-900 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mb-4">
                    {step.step}
                  </div>
                  <h5 className="font-bold text-lg text-gray-900 mb-2">{step.title}</h5>
                  <p className="text-gray-700 font-medium text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        )} */}

        {/* Ideal for */}
        {service.idealFor && service.idealFor.length > 0 && (
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6">Ideal For</h4>
            <div className="flex flex-wrap gap-3">
              {service.idealFor.map((item, idx) => (
                <span key={idx} className="inline-block bg-lime-100 text-teal-900 px-4 py-2 rounded-full text-sm font-semibold border border-lime-200">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

const WhatWeOffer = () => {
  const { data: content, loading, error } = useAPI(contentAPI.getWhatWeOfferContent, []);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash, content]);

  if (loading) {
    return (
      <>
        <Header variant="light" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-900 mx-auto mb-4"></div>
            <p className="text-xl text-gray-900 font-medium">Loading content...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header variant="light" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Content</h2>
            <p className="text-gray-900 font-medium">{error}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div>
        <section className="overflow-hidden">
          <Header variant="light" />
        </section>
      </div>

      {/* Hero Section */}
      {/* <section className="relative py-12 lg:py-24 overflow-hidden">
        <img className="absolute bottom-0 left-0" src="/fauna-assets/about/lines.svg" alt="" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-5xl sm:text-6xl mb-8 text-gray-900 font-bold">
              {content?.hero?.title || 'What We Offer'}
            </h1>
            <p className="text-2xl text-black font-medium">
              {content?.hero?.subtitle || ''}
            </p>
          </div>
        </div>
      </section> */}

      {/* Introduction */}
      {content?.introduction && (
        <section className="py-12 lg:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-heading text-4xl sm:text-5xl mb-8 text-gray-900 font-bold">
                {content.introduction.title}
              </h2>
              <p className="text-lg leading-relaxed text-gray-900 font-medium">
                {content.introduction.description}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Quick navigation */}
      {content?.services?.length > 1 && (
        <section className="pb-8">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {content.services.map((service) => (
                <a
                  key={service.slug}
                  href={`#${service.slug}`}
                  className="inline-flex px-5 py-2.5 text-sm font-semibold text-teal-900 bg-teal-50 hover:bg-teal-100 rounded-full border border-teal-200 transition duration-200"
                >
                  {service.title}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Service sections */}
      <section className="py-12 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="space-y-16 lg:space-y-24">
            {content?.services?.map((service, index) => (
              <ServiceSection key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {content?.cta && (
        <section className="py-12 lg:py-24 bg-teal-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-heading text-4xl sm:text-5xl mb-8 text-white font-bold">
                {content.cta.title}
              </h2>
              <p className="text-xl text-lime-50 font-medium mb-8">
                {content.cta.description}
              </p>
              <Link
                to={content.cta.buttonLink || '/contact'}
                className="inline-flex py-4 px-8 items-center justify-center text-lg font-bold text-teal-900 bg-lime-500 hover:bg-lime-600 rounded-full transition duration-200 mb-6"
              >
                {content.cta.buttonText}
              </Link>
              {content.cta.secondaryText && (
                <p className="text-lime-100 font-medium">{content.cta.secondaryText}</p>
              )}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
};

export default WhatWeOffer;
